import type { CatalogueViewerRow } from "./catalogueEngagementApi";
import { digitsOnlyPhone, parseActivityDate } from "./customerActivityFeed";

/** "12 s", "4 min 20 s", "1 h 05 min". Zero reads as a dash. */
export function formatViewerDuration(seconds: number | null | undefined): string {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  if (s === 0) return "—";
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h} h ${String(m).padStart(2, "0")} min`;
  return r > 0 ? `${m} min ${r} s` : `${m} min`;
}

export function viewerLastSeen(row: CatalogueViewerRow): Date | null {
  return parseActivityDate(row.lastActiveAt) || parseActivityDate(row.lastViewDate) || null;
}

/** Viewers seen since `start`, most recent first. */
export function viewersInRange(rows: CatalogueViewerRow[], start: Date): CatalogueViewerRow[] {
  return (rows || [])
    .filter((row) => {
      const seen = viewerLastSeen(row);
      return Boolean(seen && seen.getTime() >= start.getTime());
    })
    .sort((a, b) => (viewerLastSeen(b)?.getTime() || 0) - (viewerLastSeen(a)?.getTime() || 0));
}

export function viewerDisplayName(row: CatalogueViewerRow): string {
  const name = String(row.name || "").trim();
  if (name) return name;
  const phone = digitsOnlyPhone(row.phone);
  return phone ? `Buyer ···${phone.slice(-4)}` : "Buyer";
}

/** "✓ verified", "unverified" or "" for invited numbers. */
export function viewerPhoneLabel(row: CatalogueViewerRow): string {
  if (!row.phone) return "";
  if (row.phoneVerified === true) return "verified";
  if (row.phoneVerified === false) return "unverified";
  return "";
}

/** Most-viewed pieces as one line: "Kadwa red (4 min), Tanchoi blue (1 min)". */
export function topPiecesLine(row: CatalogueViewerRow, max = 2): string {
  const pieces = (row.topPieces || []).slice(0, max);
  if (!pieces.length) return "";
  return pieces
    .map((p) => {
      const label = String(p.name || p.productUniqueId || "").trim();
      const t = formatViewerDuration(p.seconds);
      return t === "—" ? label : `${label} (${t})`;
    })
    .join(", ");
}

/** Sum of time across viewers, for the metric card. */
export function totalViewerSeconds(rows: CatalogueViewerRow[]): number {
  return (rows || []).reduce((sum, r) => sum + Math.max(0, Number(r.activeSeconds) || 0), 0);
}
